import { useCallback, memo, useState } from "react";
import {
  Typography,
  Checkbox,
  MenuItem,
  Menu,
  MenuList,
  Button,
  Box,
  Divider,
  useTheme,
  ListSubheader,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useMainContext } from "../../../../../../context/useMainContext";
import saveColumnPreferences from "../../../../../../utils/saveColumnPreferences";
import { getTableHeaders } from "../../../../hooks/useTableColumnsConfig";

const ColumnsMenu = ({ columnAnchorEl, setColumnAnchorEl, handleCloseDots }) => {
  const {
    visibleColumns,
    setVisibleColumns,
    setCurrentColumnPreset,
  } = useMainContext();
  const theme = useTheme();
  const columnPopoverOpen = Boolean(columnAnchorEl);
  const columnHeaders = getTableHeaders({ columns: "all" });
  const [searchQuery, setSearchQuery] = useState("");

  const filteredColumns = columnHeaders?.filter((col) =>
    col.label.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const handleColumnVisibilityChange = useCallback(
    (col) => {
      setVisibleColumns((prev) => {
        const isCurrentlyVisible = prev.includes(col.key);
        const updated = new Set(prev);
        if (isCurrentlyVisible) {
          updated.delete(col.key);
        } else {
          updated.add(col.key);
        }
        setCurrentColumnPreset("custom");
        saveColumnPreferences({ presetKey: "custom", columns: Array.from(updated) });
        return Array.from(updated);
      });
    },
    [setVisibleColumns, setCurrentColumnPreset]
  );

  const handleClearColumns = useCallback(() => {
    setVisibleColumns([]);
    setCurrentColumnPreset("custom");
    saveColumnPreferences({ presetKey: "custom", columns: [] });
    setColumnAnchorEl(null);
    handleCloseDots();
  }, [setVisibleColumns, setCurrentColumnPreset, setColumnAnchorEl, handleCloseDots]);

  const handleClose = () => {
    setSearchQuery("");
    setColumnAnchorEl(null);
    handleCloseDots();
  };

  return (
    <Menu
      open={columnPopoverOpen}
      anchorEl={columnAnchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            overflow: "hidden",
            width: 320,
          },
        },
      }}
    >
      <MenuList
        sx={{
          overflow: "auto",
          overflowX: "hidden",
          maxHeight: `calc(${theme.components?.MuiMenu?.styleOverrides?.paper?.maxHeight || '40vh'} - 60px)`,
        }}
        subheader={
          <ListSubheader
            component="div"
            sx={{
              position: "sticky",
              top: 0,
              px: 0,
            }}
          >
            <Box sx={{ px: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </Box>
            <Divider />
          </ListSubheader>
        }
      >
        {filteredColumns?.map((col) => (
          <MenuItem
            key={col.key}
            onClick={() => handleColumnVisibilityChange(col)}
            sx={{
              px: 1,
            }}
          >
            <Checkbox checked={visibleColumns.includes(col.key)} size="small" />
            <Typography noWrap sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {col.label}
            </Typography>
          </MenuItem>
        ))}
      </MenuList>
      <Divider />
      <Box sx={{ p: 1 }}>
        <Button 
          fullWidth 
          variant="outlined" 
          onClick={handleClearColumns}
          disabled={!columnHeaders || visibleColumns.length === 0}
        >
          Clear Columns
        </Button>
      </Box>
    </Menu>
  );
};

export default memo(ColumnsMenu);
